import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';
import http from 'utils/http';

// Utils
import { createSnack } from 'snackbar/snackbarSlice';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';

const initialState = {
  confirmDialogType: '',
  customerAuthorization: {},
  customerAuthorizationToActivate: {},
  customerAuthorizationToDeactivate: {},
  customerAuthorizationToDelete: {},
  customerAuthorizations: null,
  fetchingCustomerAuthorizations: false,
  fetchingFilterValues: true,
  filterValues: {
    classes: [],
    customers: [],
    destinations: [],
    loadingControlIds: [],
    products: [],
    terminals: [],
  },
  filters: {
    authorizationStatus: 'ANY',
    authorizationType: undefined,
    controlType: 'GLOBAL',
    customerClassId: undefined,
    customerIdName: undefined,
    destinationId: undefined,
    loadingControlId: undefined,
    productIdName: undefined,
    terminalIdName: undefined,
    terminalLoadStatus: 'ANY',
  },
  isActivationDialogOpen: false,
  isConfirmDialogOpen: false,
  isFetchingCustomerAuthorization: true,
  isViewDrawerOpen: false,
  mobileFilterDrawerOpen: true,
  page: 0,
  results: 25,
  selectedAuthorizations: [],
  totalPages: 0,
  totalResults: 0,
};

const reducers = {
  resetFilters: (_, state) => R.assoc('filters', initialState.filters, state),
  setActivationDialogOpen: R.assoc('isActivationDialogOpen'),
  setConfirmDialogOpen: R.assoc('isConfirmDialogOpen'),
  setConfirmDialogType: R.assoc('confirmDialogType'),
  setCustomerAuthorization: R.assoc('customerAuthorization'),
  setCustomerAuthorizationToActivate: R.assoc('customerAuthorizationToActivate'),
  setCustomerAuthorizationToDeactivate: R.assoc('customerAuthorizationToDeactivate'),
  setCustomerAuthorizationToDelete: R.assoc('customerAuthorizationToDelete'),
  setCustomerAuthorizations: R.assoc('customerAuthorizations'),
  setFetchingCustomerAuthorizations: R.assoc('fetchingCustomerAuthorizations'),
  setFetchingFilterValues: R.assoc('fetchingFilterValues'),
  setFilter: (val, state) =>
    R.assocPath(['filters', R.prop('name', val)], R.prop('value', val), state),
  setFilterValues: R.assoc('filterValues'),
  setIsFetchingCustomerAuthorization: R.assoc('isFetchingCustomerAuthorization'),
  setIsViewDrawerOpen: R.assoc('isViewDrawerOpen'),
  setMobileFilterDrawerOpen: R.assoc('mobileFilterDrawerOpen'),
  setPageNumber: R.assoc('page'),
  setResultsPerPage: R.assoc('results'),
  setSelectedAuthorizations: R.assoc('selectedAuthorizations'),
  setTotalPages: R.assoc('totalPages'),
  setTotalResults: R.assoc('totalResults'),
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'customerAuthorizations',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

// Selectors
export const {
  resetFilters,
  setActivationDialogOpen,
  setConfirmDialogOpen,
  setConfirmDialogType,
  setCustomerAuthorization,
  setCustomerAuthorizationToActivate,
  setCustomerAuthorizationToDeactivate,
  setCustomerAuthorizationToDelete,
  setCustomerAuthorizations,
  setFetchingCustomerAuthorizations,
  setFetchingFilterValues,
  setFilter,
  setFilterValues,
  setIsFetchingCustomerAuthorization,
  setIsViewDrawerOpen,
  setLoadingControls,
  setLocations,
  setMobileFilterDrawerOpen,
  setPageNumber,
  setProducts,
  setResultsPerPage,
  setSelectedAuthorizations,
  setTotalPages,
  setTotalResults,
} = actions;

const defaultToEmpty = R.defaultTo([]);

export const terminalOptions = createSelector(
  state => state.loadingManagement.customerAuthorizations.filterValues.terminals,
  arr =>
    defaultToEmpty(
      arr.map(({ terminalIdName: value, terminalNameShort }) => ({
        label: `${value} ${terminalNameShort}`,
        value,
      }))
    )
);

export const customerOptions = createSelector(
  state => state.loadingManagement.customerAuthorizations.filterValues.customers,
  arr =>
    defaultToEmpty(
      arr.map(({ customerIdName, customerName }) => ({
        label: `${customerIdName} ${customerName}`,
        value: customerIdName,
      }))
    )
);

export const loadingControlOptions = createSelector(
  state => state.loadingManagement.customerAuthorizations.filterValues.loadingControlIds,
  arr =>
    defaultToEmpty(
      arr.map(({ loadingControlId, loadingControlIdName }) => ({
        label: loadingControlIdName,
        value: loadingControlId,
      }))
    )
);

export const productOptions = createSelector(
  state => state.loadingManagement.customerAuthorizations.filterValues.products,
  arr =>
    defaultToEmpty(
      arr.map(({ productIdName, productName }) => ({
        label: `${productIdName} ${productName}`,
        value: productIdName,
      }))
    )
);

export const classOptions = createSelector(
  state => state.loadingManagement.customerAuthorizations.filterValues.classes,
  arr =>
    defaultToEmpty(
      arr.map(({ customerClassId, customerClassName }) => ({
        label: customerClassName,
        value: customerClassId,
      }))
    )
);

export const destinationOptions = createSelector(
  state => state.loadingManagement.customerAuthorizations.filterValues.destinations,
  arr =>
    defaultToEmpty(
      arr.map(({ destinationId, destinationIdName, destinationName }) => ({
        label: `${destinationIdName} ${destinationName}`,
        value: destinationId,
      }))
    )
);

export const isClearButtonDisabled = createSelector(
  state => state.loadingManagement.customerAuthorizations.filters,
  filters => {
    const defaultState = {
      authorizationStatus: 'ANY',
      authorizationType: undefined,
      controlType: 'GLOBAL',
      customerClassId: undefined,
      customerIdName: undefined,
      destinationId: undefined,
      loadingControlId: undefined,
      productIdName: undefined,
      terminalIdName: undefined,
      terminalLoadStatus: 'ANY',
    };

    return R.equals(filters, defaultState);
  }
);

export const authorizationsToActivate = createSelector(
  state => state.loadingManagement.customerAuthorizations.selectedAuthorizations,
  arr => R.filter(auth => auth.authorizationStatus === 'Inactive', arr)
);

export const authorizationsToDeactivate = createSelector(
  state => state.loadingManagement.customerAuthorizations.selectedAuthorizations,
  arr => R.filter(auth => auth.authorizationStatus === 'Active', arr)
);

// API
let fetchFilterValuesController;
export const fetchFilterValues = currentAffiliate => dispatch => {
  dispatch(setFetchingFilterValues(true));

  if (fetchFilterValuesController) fetchFilterValuesController.abort();

  fetchFilterValuesController = new AbortController();

  const { signal } = fetchFilterValuesController;

  const query = {
    supplier: {
      companyCustomerIdName: currentAffiliate.customerIdName,
      dataCompanyId: currentAffiliate.dataCompanyId,
    },
  };

  const paramString = getStringifiedQueryParams(query);

  http
    .get(`customer-authorizations/get-customer-authorization-filter-values?${paramString}`, {
      signal,
    })
    .then(({ data }) => dispatch(setFilterValues(data)))
    .finally(() => dispatch(setFetchingFilterValues(false)));
};

let fetchCustomerAuthorizationsController;
export const fetchCustomerAuthorizations =
  ({ customerIdName: companyCustomerIdName, dataCompanyId }) =>
  (dispatch, getState) => {
    dispatch(setFetchingCustomerAuthorizations(true));
    dispatch(setCustomerAuthorizations([]));
    const { filters, page, results } = getState().loadingManagement.customerAuthorizations;

    const filtered = R.reject(R.isNil, filters);

    if (fetchCustomerAuthorizationsController) fetchCustomerAuthorizationsController.abort();

    fetchCustomerAuthorizationsController = new AbortController();

    const { signal } = fetchCustomerAuthorizationsController;

    const query = {
      ...filtered,
      page,
      results,
      supplier: {
        companyCustomerIdName,
        dataCompanyId,
      },
    };

    const paramString = getStringifiedQueryParams(query);

    http
      .get(`customer-authorizations/get-customer-authorizations?${paramString}`, { signal })
      .then(({ data }) => {
        const { customerAuthorizations, totalPages, totalResults } = data;
        dispatch(setCustomerAuthorizations(customerAuthorizations));
        dispatch(setTotalPages(totalPages));
        dispatch(setTotalResults(totalResults));
      })
      .catch(err =>
        dispatch(
          createSnack({
            message: `failed to fetch customer authorizations: ${err.response?.data.title}`,
            options: {
              variant: 'error',
            },
          })
        )
      )
      .finally(() => dispatch(setFetchingCustomerAuthorizations(false)));
  };

let fetchCustomerAuthorizationController;
export const fetchCustomerAuthorization = params => dispatch => {
  dispatch(setIsFetchingCustomerAuthorization(true));

  if (fetchCustomerAuthorizationController) fetchCustomerAuthorizationController.abort();
  fetchCustomerAuthorizationController = new AbortController();
  const { signal } = fetchCustomerAuthorizationController;

  const paramString = getStringifiedQueryParams(params);

  http
    .get(`customer-authorizations/view-customer-authorization?${paramString}`, { signal })
    .then(({ data }) => dispatch(setCustomerAuthorization(data)))
    .catch(err =>
      dispatch(
        createSnack({
          message: `failed to fetch customer authorization: ${err.response?.data.title}`,
          options: {
            variant: 'error',
          },
        })
      )
    )
    .finally(() => dispatch(setIsFetchingCustomerAuthorization(false)));
};

let activateController;
export const activateAuthorizations = currentAffiliate => (dispatch, getState) => {
  const { customerAuthorizationToActivate, selectedAuthorizations } =
    getState().loadingManagement.customerAuthorizations;

  const authsToActivate = authorizationsToActivate(getState());

  if (activateController) activateController.abort();
  activateController = new AbortController();
  const { signal } = activateController;

  const customerAuthorizationIds = R.isEmpty(customerAuthorizationToActivate)
    ? authsToActivate.map(({ customerId }) => customerId)
    : [customerAuthorizationToActivate.customerId];

  http
    .post(
      'customer-authorizations/activate-customer-authorizations',
      { customerAuthorizationIds },
      { signal }
    )
    .then(({ data }) =>
      dispatch(
        createSnack({
          message: R.isEmpty(data.invalidResponses) ? data.validResponse : data.invalidResponses,
          options: data.validResponse
            ? {}
            : data.invalidResponses
            ? { variant: 'warning' }
            : undefined,
        })
      )
    )
    .catch(err =>
      dispatch(
        createSnack({
          message: `failed to activate customer authorization(s): ${err.response?.data.title}`,
          options: {
            variant: 'error',
          },
        })
      )
    )
    .finally(() => {
      if (R.isEmpty(customerAuthorizationToActivate)) {
        dispatch(
          setSelectedAuthorizations(
            // filter out newly activated customer auths
            R.reject(
              ({ customerId }) => R.includes(customerId, customerAuthorizationIds),
              selectedAuthorizations
            )
          )
        );
      } else {
        dispatch(
          setSelectedAuthorizations(
            R.reject(
              ({ customerId }) => R.equals(customerId, customerAuthorizationToActivate.customerId),
              selectedAuthorizations
            )
          )
        );
        dispatch(setCustomerAuthorizationToActivate({}));
      }

      dispatch(setActivationDialogOpen(false));
      dispatch(fetchCustomerAuthorizations(currentAffiliate));
    });
};

let deactivateController;
export const deactivateAuthorizations = currentAffiliate => (dispatch, getState) => {
  const { customerAuthorizationToDeactivate, selectedAuthorizations } =
    getState().loadingManagement.customerAuthorizations;

  const authsToDeactivate = authorizationsToDeactivate(getState());

  if (deactivateController) deactivateController.abort();
  deactivateController = new AbortController();
  const { signal } = deactivateController;

  const customerAuthorizationIds = R.isEmpty(customerAuthorizationToDeactivate)
    ? authsToDeactivate.map(({ customerId }) => customerId)
    : [customerAuthorizationToDeactivate.customerId];

  http
    .post(
      'customer-authorizations/deactivate-customer-authorizations',
      { customerAuthorizationIds },
      { signal }
    )
    .then(({ data }) =>
      dispatch(
        createSnack({
          message: data,
        })
      )
    )
    .catch(err =>
      dispatch(
        createSnack({
          message: `failed to deactivate customer authorization(s): ${err.response?.data.title}`,
          options: {
            variant: 'error',
          },
        })
      )
    )
    .finally(() => {
      dispatch(setConfirmDialogOpen(false));
      if (R.isEmpty(customerAuthorizationToDeactivate)) {
        dispatch(
          setSelectedAuthorizations(
            // filter out newly deactivated customer auths
            R.reject(
              ({ customerId }) => R.includes(customerId, customerAuthorizationIds),
              selectedAuthorizations
            )
          )
        );
      } else {
        dispatch(
          setSelectedAuthorizations(
            R.reject(
              ({ customerId }) =>
                R.equals(customerId, customerAuthorizationToDeactivate.customerId),
              selectedAuthorizations
            )
          )
        );
        dispatch(setCustomerAuthorizationToDeactivate({}));
      }

      dispatch(setConfirmDialogType(''));
      dispatch(fetchCustomerAuthorizations(currentAffiliate));
    });
};

let deleteCustomerAuthorizationController;
export const deleteCustomerAuthorizations = currentAffiliate => (dispatch, getState) => {
  const { customerAuthorizationToDelete, selectedAuthorizations } =
    getState().loadingManagement.customerAuthorizations;

  if (deleteCustomerAuthorizationController) deleteCustomerAuthorizationController.abort();
  deleteCustomerAuthorizationController = new AbortController();
  const { signal } = deleteCustomerAuthorizationController;

  const deleteId =
    customerAuthorizationToDelete.customerId ??
    customerAuthorizationToDelete.customerAuthorizationId;

  const customerAuthorizationIds = R.isEmpty(customerAuthorizationToDelete)
    ? selectedAuthorizations.map(({ customerId }) => customerId)
    : [deleteId];

  http
    .post(
      'customer-authorizations/remove-customer-authorization',
      { customerAuthorizationIds },
      { signal }
    )
    .then(({ data }) =>
      dispatch(
        createSnack({
          message: data.removedCustomerAuthorizationsMessage,
        })
      )
    )
    .catch(err =>
      dispatch(
        createSnack({
          message: `failed to delete customer authorization(s): ${err.response?.data.title}`,
          options: {
            variant: 'error',
          },
        })
      )
    )
    .finally(() => {
      dispatch(setConfirmDialogOpen(false));
      if (R.isEmpty(customerAuthorizationToDelete)) {
        dispatch(setSelectedAuthorizations([]));
      }
      if (!R.isEmpty(customerAuthorizationToDelete)) {
        dispatch(setCustomerAuthorizationToDelete({}));
        dispatch(
          setSelectedAuthorizations(
            R.reject(({ customerId }) => R.equals(customerId, deleteId), selectedAuthorizations)
          )
        );
      }
      dispatch(setConfirmDialogType(''));
      dispatch(setIsViewDrawerOpen(false));
      dispatch(fetchCustomerAuthorizations(currentAffiliate));
    });
};

export default reducer;
